/* @jsx h */
import { defineComponent, h, ref, computed, onMounted, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { getAgent } from "../stub-data";
import { AGENT_COLOR_MAP, PROJECT_COLOR_MAP } from "../renderers/index";
import { useIntelligenceStore } from "../store";
import { ProjectCreateModal } from "./ProjectCreateModal";
import type { Project, Thread, MerchantAgent } from "../types";

export const AgentAvatar = defineComponent<{
  agent: MerchantAgent;
  size?: "xs" | "sm" | "md";
}>({
  name: "AgentAvatar",
  props: ["agent", "size"],
  setup(props) {
    return () => {
      const size = props.size ?? "sm";
      const dim =
        size === "xs" ? "w-5 h-5" : size === "sm" ? "w-6 h-6" : "w-8 h-8";
      const iconSize = size === "xs" ? "11px" : size === "sm" ? "13px" : "16px";
      const colors = AGENT_COLOR_MAP[props.agent.avatarColor];

      return (
        <div
          class={`${dim} rounded-md flex items-center justify-center flex-shrink-0 border ${colors.bg} ${colors.border}`}
          title={props.agent.name}
        >
          <span
            class={`material-symbols-outlined ${colors.text}`}
            style={`font-size:${iconSize}`}
          >
            {props.agent.avatarIcon}
          </span>
        </div>
      );
    };
  },
});

const SidebarIconButton = defineComponent<{
  icon: string;
  title: string;
  onClick?: (event: MouseEvent) => void;
}>({
  name: "SidebarIconButton",
  props: ["icon", "title", "onClick"],
  setup(props) {
    return () => (
      <button
        onClick={props.onClick}
        title={props.title}
        class="w-6 h-6 rounded-md flex items-center justify-center text-base-content/35 hover:text-base-content/70 hover:bg-base-content/10 transition-colors"
      >
        <span class="material-symbols-outlined" style="font-size:15px">
          {props.icon}
        </span>
      </button>
    );
  },
});

const ThreadItem = defineComponent<{
  thread: Thread;
  projectId: string;
  isActive: boolean;
}>({
  name: "ThreadItem",
  props: ["thread", "projectId", "isActive"],
  setup(props) {
    const store = useIntelligenceStore();
    const router = useRouter();
    const modelMeta = computed(() => {
      const modelId = props.thread.modelId ?? "@cf/moonshotai/kimi-k2.7-code";
      return store.models.value.find((m) => m.id === modelId) ?? null;
    });

    const handleRename = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      const title = window.prompt("Rename thread", props.thread.title)?.trim();
      if (!title || title === props.thread.title) return;
      store.renameThread(props.projectId, props.thread.id, title);
    };

    const handleDelete = async (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      const ok = window.confirm(
        "Delete this thread? This removes all its messages and attachments and can't be undone.",
      );
      if (!ok) return;
      await store.deleteThread(props.projectId, props.thread.id);
      if (props.isActive) {
        router.push(
          `/app/intelligence/projects/${props.projectId}/threads/new`,
        );
      }
    };

    return () => {
      const meta = modelMeta.value;
      const costColor =
        meta?.costTier === "low"
          ? "text-success/80"
          : meta?.costTier === "medium"
            ? "text-warning/80"
            : "text-error/80";

      return (
        <div
          class={[
            "group grid grid-cols-[14px_minmax(0,1fr)_auto] items-start gap-2 rounded-md py-0.5 pl-2 pr-1 text-left transition-colors",
            props.isActive
              ? "bg-base-content/10 text-base-content"
              : "text-base-content/55 hover:bg-base-content/5 hover:text-base-content/80",
          ].join(" ")}
        >
          <RouterLink
            to={`/app/intelligence/projects/${props.projectId}/threads/${props.thread.id}`}
            class="col-span-2 grid grid-cols-[14px_minmax(0,1fr)] items-start gap-2 min-w-0"
          >
            <span
              class={`material-symbols-outlined mt-0.5 ${props.isActive ? "text-accent" : "text-base-content/25"}`}
              style="font-size:14px"
            >
              chat_bubble
            </span>
            <div class="min-w-0 flex flex-col">
              <span class="truncate text-[12.5px] leading-5">
                {props.thread.title}
              </span>
              {meta && (
                <div class="flex items-center gap-1.5 mt-0.5 text-[9.5px] text-base-content/40 font-medium tracking-wide">
                  <span class="capitalize bg-base-content/5 px-1 py-0.5 rounded text-base-content/60">
                    {meta.provider}
                  </span>
                  <span class={`font-mono ${costColor}`}>{meta.costTier}</span>
                </div>
              )}
            </div>
          </RouterLink>
          <div class="dropdown dropdown-end hidden flex-shrink-0 group-hover:block">
            <button
              tabindex={0}
              class="flex h-5 w-5 items-center justify-center rounded text-base-content/35 hover:bg-base-content/10 hover:text-base-content/70"
            >
              <span class="material-symbols-outlined" style="font-size:14px">
                more_vert
              </span>
            </button>
            <ul
              tabindex={0}
              class="dropdown-content menu z-10 w-36 rounded-md border border-base-content/10 bg-base-100 p-1 text-base-content shadow-md"
            >
              <li>
                <a onClick={handleRename}>
                  <span
                    class="material-symbols-outlined"
                    style="font-size:14px"
                  >
                    edit
                  </span>
                  Rename
                </a>
              </li>
              <li>
                <a onClick={handleDelete} class="text-error">
                  <span
                    class="material-symbols-outlined"
                    style="font-size:14px"
                  >
                    delete
                  </span>
                  Delete
                </a>
              </li>
            </ul>
          </div>
        </div>
      );
    };
  },
});

const MiniAgentStack = defineComponent<{ agentIds: string[] }>({
  name: "MiniAgentStack",
  props: ["agentIds"],
  setup(props) {
    const agents = computed(
      () =>
        props.agentIds
          .slice(0, 4)
          .map((id) => getAgent(id))
          .filter(Boolean) as MerchantAgent[],
    );

    return () => (
      <div class="flex items-center gap-1">
        {agents.value.map((agent, index) => (
          <div key={agent.id}>
            <AgentAvatar agent={agent} size="xs" />
          </div>
        ))}
        {props.agentIds.length > 4 && (
          <div class="w-5 h-5 rounded-md border border-base-content/10 bg-base-100 flex items-center justify-center text-[9px] font-semibold text-base-content/45">
            +{props.agentIds.length - 4}
          </div>
        )}
      </div>
    );
  },
});

const ProjectItem = defineComponent<{
  project: Project;
  expanded: boolean;
  activeThreadId: string | null;
  onToggle: () => void;
}>({
  name: "ProjectItem",
  props: ["project", "expanded", "activeThreadId", "onToggle"],
  setup(props) {
    const router = useRouter();
    const route = useRoute();
    const store = useIntelligenceStore();
    const isProjectActive = computed(
      () => route.params.projectId === props.project.id,
    );
    const colors = computed(
      () => PROJECT_COLOR_MAP[props.project.color] ?? PROJECT_COLOR_MAP.neutral,
    );
    const sortedThreads = computed(() =>
      [...props.project.threads].sort(
        (a, b) =>
          new Date(b.lastMessageAt || 0).getTime() -
          new Date(a.lastMessageAt || 0).getTime(),
      ),
    );

    const handleSettings = (event: MouseEvent) => {
      event.stopPropagation();
      router.push(`/app/intelligence/projects/${props.project.id}/settings`);
    };

    const handleNewThread = async (event: MouseEvent) => {
      event.stopPropagation();
      const thread = await store.createThread(props.project.id);
      router.push(
        `/app/intelligence/projects/${props.project.id}/threads/${thread.id}`,
      );
    };

    return () => (
      <div class="mb-1">
        <div
          class={[
            "group flex items-center gap-1.5 rounded-md px-2 py-0.5 cursor-pointer select-none transition-colors",
            isProjectActive.value
              ? "bg-base-content/10 text-base-content"
              : "text-base-content/70 hover:bg-base-content/5",
          ].join(" ")}
          onClick={props.onToggle}
        >
          <span
            class="material-symbols-outlined text-base-content/35 flex-shrink-0 transition-transform duration-150"
            style={`font-size:15px; transform:rotate(${props.expanded ? 90 : 0}deg)`}
          >
            chevron_right
          </span>
          <span
            class={`w-6 h-6 rounded-md border flex items-center justify-center text-[13px] ${colors.value.soft}`}
          >
            {props.project.icon}
          </span>
          <span class="min-w-0 flex-1 truncate text-[13px] font-medium leading-5">
            {props.project.name}
          </span>
          <div class="hidden items-center gap-0.5 group-hover:flex">
            <SidebarIconButton
              icon="edit_square"
              title="New thread"
              onClick={handleNewThread}
            />
            <SidebarIconButton
              icon="more_horiz"
              title="Project settings"
              onClick={handleSettings}
            />
          </div>
        </div>

        {props.expanded && (
          <div class="ml-[27px] mt-1 flex flex-col gap-0.5">
            {sortedThreads.value.length === 0 ? (
              <p class="px-2 py-1 text-[12px] text-base-content/25">
                No threads yet
              </p>
            ) : (
              sortedThreads.value.map((thread) => (
                <ThreadItem
                  key={thread.id}
                  thread={thread}
                  projectId={props.project.id}
                  isActive={props.activeThreadId === thread.id}
                />
              ))
            )}
          </div>
        )}
      </div>
    );
  },
});

const SidebarSkeleton = defineComponent({
  name: "SidebarSkeleton",
  setup() {
    return () => (
      <div class="flex flex-col gap-2 px-2 pt-1">
        {[0, 1, 2].map((i) => (
          <div key={i} class="flex flex-col gap-1.5">
            <div class="flex items-center gap-2 px-2 py-0.5">
              <div class="skeleton h-3 w-3 rounded" />
              <div class="skeleton h-6 w-6 rounded-md" />
              <div class="skeleton h-3 flex-1 rounded" />
            </div>
            {i === 0 && (
              <div class="ml-[27px] flex flex-col gap-1.5">
                <div class="skeleton h-3 w-[85%] rounded" />
                <div class="skeleton h-3 w-[70%] rounded" />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  },
});

export const AgentSidebar = defineComponent({
  name: "AgentSidebar",
  setup() {
    const route = useRoute();
    const store = useIntelligenceStore();
    const { projects, refresh, loaded } = store;
    const activeProjectId = computed(
      () => (route.params.projectId as string) || null,
    );
    const activeThreadId = computed(
      () => (route.params.threadId as string) || null,
    );
    const expanded = ref<Set<string>>(new Set());
    const showCreateModal = ref(false);

    onMounted(async () => {
      await refresh();
      expanded.value = new Set(
        activeProjectId.value
          ? [activeProjectId.value]
          : projects.value[0]
            ? [projects.value[0].id]
            : [],
      );
    });

    watch(activeProjectId, (id) => {
      if (!id) return;
      const next = new Set(expanded.value);
      next.add(id);
      expanded.value = next;
    });

    const toggleProject = (id: string) => {
      const next = new Set(expanded.value);
      next.has(id) ? next.delete(id) : next.add(id);
      expanded.value = next;
    };

    return () => (
      <aside class="flex h-full w-[260px] flex-shrink-0 flex-col overflow-hidden border-r border-base-content/10 bg-base-200/80">
        <div class="flex items-center justify-between px-3 py-3">
          <RouterLink
            to="/app/intelligence"
            class="flex min-w-0 items-center gap-2 rounded-md px-1 py-1 text-base-content/80 hover:bg-base-content/5"
          >
            <span
              class="material-symbols-outlined text-accent"
              style="font-size:18px"
            >
              auto_awesome
            </span>
            <span class="truncate text-[13px] font-semibold text-accent">
              Intelligence
            </span>
          </RouterLink>
          <SidebarIconButton
            icon="add"
            title="New project"
            onClick={() => (showCreateModal.value = true)}
          />
        </div>

        <div class="px-3 pb-2">
          <div class="flex items-center gap-2 rounded-md border border-base-content/10 bg-base-100/55 px-2 py-1.5 text-base-content/35">
            <span
              class="material-symbols-outlined text-accent/70"
              style="font-size:14px"
            >
              search
            </span>
            <span class="text-[12px]">Search projects and threads</span>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto px-2 pb-3">
          <RouterLink
            to="/app/intelligence"
            class={[
              "mb-2 flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] transition-colors",
              !activeProjectId.value
                ? "bg-base-content/10 text-base-content"
                : "text-base-content/60 hover:bg-base-content/5 hover:text-base-content/80",
            ].join(" ")}
          >
            <span
              class={`material-symbols-outlined ${!activeProjectId.value ? "text-accent" : "text-base-content/40"}`}
              style="font-size:15px"
            >
              home
            </span>
            Home
          </RouterLink>

          <div class="mb-1 px-2 text-[11px] font-medium uppercase tracking-wide text-accent/70">
            Projects
          </div>
          {!loaded.value ? (
            <SidebarSkeleton />
          ) : (
            <>
              {projects.value.map((project) => (
                <ProjectItem
                  key={project.id}
                  project={project}
                  expanded={expanded.value.has(project.id)}
                  activeThreadId={activeThreadId.value}
                  onToggle={() => toggleProject(project.id)}
                />
              ))}

              <button
                onClick={() => (showCreateModal.value = true)}
                class="mt-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] text-accent/70 hover:bg-base-content/5 hover:text-accent"
              >
                <span class="material-symbols-outlined" style="font-size:15px">
                  add
                </span>
                New project
              </button>
            </>
          )}
        </div>

        <div class="border-t border-base-content/10 px-3 py-3">
          <RouterLink
            to="/app/intelligence"
            class="flex items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] text-base-content/50 hover:bg-base-content/5 hover:text-base-content/80"
          >
            <span
              class="material-symbols-outlined text-accent/70"
              style="font-size:15px"
            >
              smart_toy
            </span>
            Agent catalogue
          </RouterLink>
        </div>

        {showCreateModal.value && (
          <ProjectCreateModal
            onClose={() => (showCreateModal.value = false)}
            onCreated={refresh}
          />
        )}
      </aside>
    );
  },
});
